import { useEffect } from "react";
import { createPortal } from "react-dom";

export interface CertificateAsset {
  /** 表示タイトル（許可・資格名） */
  title: string;
  /** /static/... の絶対パス（URLエンコード済み） */
  url: string;
  /** "pdf" / "image" / "docx" などプレビュー方式の決定用 */
  kind: "pdf" | "image" | "docx" | "other";
  /** 補足情報（登録番号など） */
  caption?: string;
}

interface Props {
  asset: CertificateAsset | null;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2147483000, // 最大値付近 (32bit intの上限近く)
  padding: 0,
};

const dialogStyle: React.CSSProperties = {
  position: "relative",
  width: "min(1200px, 96vw)",
  height: "96vh",
  background: "white",
  borderRadius: 8,
  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 20px",
  borderBottom: "1px solid #e5e7eb",
  background: "#f9fafb",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#111827",
};

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
  color: "#6b7280",
  padding: 4,
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#1f2937",
  overflow: "auto",
};

const downloadButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 24px",
  background: "#0d6efd",
  color: "white",
  borderRadius: 6,
  textDecoration: "none",
  fontWeight: 500,
  margin: 16,
};

/**
 * 証明書プレビュー用モーダル。
 * - 背景クリックで閉じる
 * - ESCで閉じる
 * - PDFは <iframe>、画像は <img>、docx などはダウンロードリンク
 */
export const CertificateModal: React.FC<Props> = ({ asset, onClose }) => {
  // ESCで閉じる + 開いている間はbodyスクロールを止める
  useEffect(() => {
    if (!asset) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [asset, onClose]);

  if (!asset) return null;

  // 背景クリック（dialog外）で閉じる
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Portal で body 直下にマウントすることで、親のスタッキングコンテキストに影響されず
  // 確実にすべての要素の上に表示する
  return createPortal(
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={`${asset.title} 証明書`}
      onClick={handleOverlayClick}
    >
      <div style={dialogStyle}>
        <header style={headerStyle}>
          <div>
            <h2 style={titleStyle}>{asset.title}</h2>
            {asset.caption && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {asset.caption}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={closeButtonStyle}
            aria-label="閉じる"
            title="閉じる (ESC)"
          >
            ×
          </button>
        </header>
        <div style={bodyStyle}>
          {asset.kind === "pdf" && (
            <iframe
              src={asset.url}
              title={asset.title}
              style={{ width: "100%", height: "100%", border: "none", background: "white" }}
            />
          )}
          {asset.kind === "image" && (
            <img
              src={asset.url}
              alt={asset.title}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          )}
          {(asset.kind === "docx" || asset.kind === "other") && (
            <div style={{ textAlign: "center", color: "white" }}>
              <p style={{ marginBottom: 8 }}>
                ブラウザではプレビューできない形式です。下のボタンからダウンロードしてください。
              </p>
              <a
                href={asset.url}
                download={asset.title + (asset.kind === "docx" ? ".docx" : "")}
                style={downloadButtonStyle}
              >
                <span aria-hidden="true">⬇</span>
                資料をダウンロード
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CertificateModal;
